/**
 * ProShip Direct — the page a merchant sees before they buy.
 *
 * Three jobs, in this order: say what Direct is, show honestly how it differs from shipping on
 * ProShip's couriers, and price it. The comparison table is the part that earns trust — a
 * merchant deciding this is choosing between two commercial arrangements, and hiding the
 * trade-offs would only produce churn later. The facts come from the comparison Ashvini built
 * into Operating Mode, which was the genuinely useful half of that screen.
 */
import React from 'react';
import {
  Box, Grid, Typography, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

import { sectionCardSx, sectionTitleSx, primaryBtnSx, secondaryBtnSx, pageHeroSx, heroTitleSx, heroSubSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, WHITE, LIGHT_BG, PRIMARY_BLUE, SUCCESS, PURPLE, ORANGE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { tHeadSx, tCellSx, statLabelSx } from './stats';
import { CUSTOM_PLAN, DIRECT_VS_PROSHIP, GST_RATE, planGst, planTotal } from '../data/plans';
import { OwnerChip } from './chips';

const bodySx = { fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.55 };
const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.45 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const BENEFITS = [
  'Ship on courier contracts you negotiated yourself',
  'Your own couriers and ProShip’s, allocated together per order',
  'Courier rules and PACE allocation across both pools',
];

const planCardSx = (popular) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: WHITE,
  border: `1px solid ${popular ? PRIMARY_BLUE : BORDER}`,
  borderRadius: RADIUS.lg,
  overflow: 'hidden',
  boxShadow: popular ? `0 0 0 1px ${PRIMARY_BLUE}` : 'none',
});

function PlanCard({ plan, onChoose, disabled }) {
  return (
    <Box sx={planCardSx(plan.popular)}>
      <Box sx={{ px: 1.75, pt: 1.75, pb: 1.25, bgcolor: plan.popular ? `${PRIMARY_BLUE}0A` : LIGHT_BG }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 0.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
            {plan.name}
          </Typography>
          {plan.popular && (
            <Chip
              size='small'
              label='Most chosen'
              sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, height: 18, bgcolor: `${PRIMARY_BLUE}18`, color: PRIMARY_BLUE }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xl'], fontWeight: WEIGHT.bold, color: NAVY }}>
            {plan.price === 0 ? 'Free' : inr(plan.price)}
          </Typography>
          {plan.price > 0 && <Typography sx={smallSx}>/ month</Typography>}
        </Box>
        <Typography sx={smallSx}>{plan.blurb}</Typography>
      </Box>

      <Box sx={{ px: 1.75, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
        {[
          { k: 'Shipments included', v: plan.shipments.toLocaleString('en-IN') },
          { k: 'Over-usage, per shipment', v: `₹${plan.overagePrice.toFixed(2)}` },
          { k: 'Courier contracts', v: plan.courierIntegrations },
          // Label the rate the plan actually carries, not a hardcoded 18% — gstRate is a
          // negotiable term, so the number beside it must match what the wallet is debited.
          {
            k: `GST (${Math.round(Number(plan.gstRate ?? GST_RATE) * 100)}%)`,
            v: plan.price === 0 ? '—' : inr(planGst(plan)),
          },
          { k: 'Total payable', v: plan.price === 0 ? 'Free' : inr(planTotal(plan)), strong: true },
        ].map((row) => (
          <Box key={row.k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.3 }}>
            <Typography sx={smallSx}>{row.k}</Typography>
            <Typography sx={row.strong ? strongSx : { ...smallSx, color: NAVY }}>{row.v}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 1.75, py: 1.25, flex: 1 }}>
        {plan.features.map((f) => (
          <Box key={f} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', py: 0.3 }}>
            <CheckCircleIcon sx={{ fontSize: 14, color: SUCCESS, mt: '2px' }} />
            <Typography sx={smallSx}>{f}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 1.75, pb: 1.75 }}>
        <Button
          fullWidth
          variant='contained'
          size='small'
          sx={primaryBtnSx}
          disabled={disabled}
          onClick={() => onChoose(plan)}
        >
          {plan.price === 0 ? 'Start trial' : 'Activate plan'}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * `plans` now comes from the caller (GET direct/plans via usePlan), not a fixture. CUSTOM_PLAN
 * and DIRECT_VS_PROSHIP stay local — they are marketing copy with no backend owner.
 *
 * An empty list renders the "nothing to show" state rather than an empty grid: it is what a
 * merchant sees when the catalog is still loading, or when Direct is not enabled for them.
 */
export default function DirectMarketing({ plans = [], onChoose, disabled }) {
  return (
    <>
      <Box sx={pageHeroSx}>
        <Typography sx={heroTitleSx}>Your couriers. Our software.</Typography>
        <Typography sx={heroSubSx}>
          Already negotiated your own courier contracts? Bring them onto ProShip and keep the
          rates you agreed — with allocation, tracking and NDR across your couriers and ours
          together.
        </Typography>
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {BENEFITS.map((b) => (
            <Box key={b} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <CheckCircleIcon sx={{ fontSize: 15, color: SUCCESS, mt: '2px' }} />
              <Typography sx={bodySx}>{b}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* The honest comparison — what changes, and what it costs you */}
      <Box sx={sectionCardSx}>
        <Typography sx={sectionTitleSx}>How Direct differs from shipping on ProShip</Typography>
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tHeadSx, minWidth: 170 }}>&nbsp;</TableCell>
                <TableCell sx={{ ...tHeadSx, minWidth: 230 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <OwnerChip owner='DIRECT' /> With ProShip Direct
                  </Box>
                </TableCell>
                <TableCell sx={{ ...tHeadSx, minWidth: 230 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <OwnerChip owner='PROSHIP' /> On ProShip couriers
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DIRECT_VS_PROSHIP.map((row, i) => {
                const last = i === DIRECT_VS_PROSHIP.length - 1;
                const edge = last ? { borderBottom: 'none' } : {};
                return (
                  <TableRow key={row.fact} hover>
                    <TableCell sx={{ ...tCellSx, ...edge, color: SUBTLE, whiteSpace: 'normal' }}>{row.fact}</TableCell>
                    <TableCell sx={{ ...tCellSx, ...edge, whiteSpace: 'normal', fontWeight: WEIGHT.semibold, borderLeft: `2px solid ${ORANGE}22` }}>
                      {row.direct}
                    </TableCell>
                    <TableCell sx={{ ...tCellSx, ...edge, whiteSpace: 'normal', borderLeft: `2px solid ${PURPLE}22` }}>
                      {row.proship}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Typography sx={{ ...smallSx, mt: 1 }}>
          You do not have to choose one. Most brands run both and move lanes across as their own
          contracts prove out.
        </Typography>
      </Box>

      {/* Plans */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: GAP.sm, mb: 1 }}>
          <Typography sx={sectionTitleSx}>Plans</Typography>
          <Typography sx={smallSx}>Monthly, cancel any time</Typography>
        </Box>
        <Grid container spacing={1.5} alignItems='stretch'>
          {plans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <PlanCard plan={plan} onChoose={onChoose} disabled={disabled} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={sectionCardSx}>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} md={8}>
            <Typography sx={strongSx}>{CUSTOM_PLAN.title}</Typography>
            <Typography sx={bodySx}>{CUSTOM_PLAN.body}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: { md: 'flex-end' } }}>
              <Button
                size='small'
                href={`mailto:${CUSTOM_PLAN.contact}`}
                startIcon={<MailOutlineIcon sx={{ fontSize: 15 }} />}
                sx={secondaryBtnSx}
              >
                {CUSTOM_PLAN.contact}
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ my: 1.5, borderColor: BORDER }} />
        <Typography sx={statLabelSx}>Not included</Typography>
        <Typography sx={{ ...smallSx, mt: 0.25 }}>
          Buyer communications — WhatsApp, SMS and email notifications — are metered separately and
          switched on from Settings. Plan prices above cover the Direct platform only.
        </Typography>
      </Box>
    </>
  );
}
